import { LayerProps } from 'react-map-gl';

// Brand gold, matches tailwind.config.js `primary`.
const GOLD = '#BBAA64';
const BLACK = '#090909';

// The pin art is 30x42. Two surfaces draw it — the `unclustered-point` symbol layer below, and
// the bouncing marker Explore mounts on whichever course is open — and they have to scale it
// identically, or a club's pin visibly changes size at the moment it is picked. One set of stops,
// read by both.
export const COURSE_PIN_WIDTH = 30;
export const COURSE_PIN_HEIGHT = 42;

// Smaller far out, fuller size once the visitor is actually looking at a region: 0.5 is a 15x21
// pin and 0.8 a 24x34 one. A marker that reads well on a globe is oversized on a street map, and
// the other way round.
const PIN_SIZE_STOPS = {
  minZoom: 4,
  minScale: 0.5,
  maxZoom: 12,
  maxScale: 0.8,
};

// The JS reading of the `icon-size` expression built from the same stops below. Mapbox clamps an
// `interpolate` outside its end stops, so this clamps too.
export const coursePinScaleForZoom = (zoom: number): number => {
  const { minZoom, minScale, maxZoom, maxScale } = PIN_SIZE_STOPS;
  const progress = Math.min(
    Math.max((zoom - minZoom) / (maxZoom - minZoom), 0),
    1,
  );
  return minScale + progress * (maxScale - minScale);
};

export const clusterLayer: LayerProps = {
  id: 'clusters',
  type: 'circle',
  source: 'courses',
  filter: ['has', 'point_count'],
  paint: {
    'circle-color': GOLD,
    'circle-radius': ['step', ['get', 'point_count'], 18, 100, 26, 750, 34],
    'circle-stroke-color': BLACK,
    'circle-stroke-width': 1.5,
  },
};

export const clusterCountLayer: LayerProps = {
  id: 'cluster-count',
  type: 'symbol',
  source: 'courses',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    'text-size': 14,
  },
  paint: {
    'text-color': '#FFFFFF',
  },
};

// The name the pin PNG is registered under in the Mapbox style. Explore adds the image on map
// load and again on `styleimagemissing`; this layer only names it. If the image is not there the
// layer draws nothing at all — no fallback, no error on screen — so those two hooks matter.
export const COURSE_PIN_IMAGE = 'gp-course-pin';

// The single-course pins. The one that is currently open is drawn but not painted.
//
// Explore draws the open course as its own bouncing DOM marker, so painting it here as well would
// leave a static pin sitting under the moving one and show two pins for one club at the top of
// every hop. It is hidden with `icon-opacity`, not removed with a filter.
//
// That distinction is the whole point. A filtered-out feature is not in the layer, so
// queryRenderedFeatures cannot find it, and the map's click handler reads that layer. The
// bouncing marker over it is deliberately click-through (see the :has() rule in index.css), so
// with the feature filtered away the open course's pin answered to nothing at all: close the
// drawer, click the same pin, and nothing happened, because there was nothing under the cursor to
// find. An invisible icon is still a rendered feature, so the click lands and the drawer reopens.
//
// The id comparison is case-insensitive because these are GUIDs and the casing the snapshot
// carries is not guaranteed to match the selection's. `to-string` is what makes it safe on a
// feature with no id at all; `downcase` on its own throws there rather than returning false.
export const buildUnclusteredPointLayer = (
  selectedCourseId?: string | null,
): LayerProps => ({
  id: 'unclustered-point',
  type: 'symbol',
  source: 'courses',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'icon-opacity': selectedCourseId
      ? [
          'case',
          [
            '==',
            ['downcase', ['to-string', ['get', 'id']]],
            selectedCourseId.toLowerCase(),
          ],
          0,
          1,
        ]
      : 1,
  },
  layout: {
    'icon-image': COURSE_PIN_IMAGE,
    // The pin is a teardrop: its tip is the location, not its middle. Anchoring to the bottom is
    // what puts that tip on the coordinate — the default (`center`) would float every course half
    // a pin north of where it actually is.
    'icon-anchor': 'bottom',
    // Symbol layers hide colliding icons by default. Circles never did, so without this the map
    // would silently drop courses that sit close together and look like data was missing.
    'icon-allow-overlap': true,
    // The other half of that. `allow-overlap` stops the pin from being hidden by its neighbours;
    // `ignore-placement` stops it from *hiding* them — without it every pin occupies space in
    // Mapbox's collision index and suppresses the course-name labels beside it, so a dense region
    // renders as gold pins with no names. A circle layer never entered that index at all.
    'icon-ignore-placement': true,
    'icon-size': [
      'interpolate',
      ['linear'],
      ['zoom'],
      PIN_SIZE_STOPS.minZoom,
      PIN_SIZE_STOPS.minScale,
      PIN_SIZE_STOPS.maxZoom,
      PIN_SIZE_STOPS.maxScale,
    ],
  },
});

// The course name under each pin.
//
// It takes the ground it is painted on, because the visitor can now change that ground (see
// exploreMapStyles.ts). Near-white on a dark map, near-black on a light one, with the halo the
// other way round each time. Fixed near-white was fine while Monochrome was the only style; over
// the Outdoors style's pale fields it was a white word with a 1px outline, which is unreadable.
//
// The halo does most of the work here, not the fill: it is what keeps a name legible over
// satellite imagery, which is neither light nor dark and changes under every label.
export const buildUnclusteredPointLabelLayer = (
  isLightGround: boolean,
): LayerProps => ({
  id: 'unclustered-point-label',
  type: 'symbol',
  source: 'courses',
  filter: ['!', ['has', 'point_count']],
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['Arial Unicode MS Bold'],
    'text-size': 11,
    'text-offset': [0, 1.4],
    'text-anchor': 'top',
  },
  paint: {
    'text-color': isLightGround ? BLACK : '#f3f3f3',
    'text-halo-color': isLightGround ? '#FFFFFF' : BLACK,
    // Wider on a light ground: a white halo has to carry the name across pale grass and pale
    // road fills, where a dark one only has to separate it from a dark background.
    'text-halo-width': isLightGround ? 1.5 : 1,
  },
});
